import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminAccessEditorFields,
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanAdminOperatorAccess,
  fetchYowanAdminMemberDetail,
  fetchYowanAdminMemberOptions,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const operatorAccessMemberPlaceholderOptions = [{ value: '', label: '対象会員を選択' }];

const getOperatorAccessCreateErrorMessage = (error, content) => {
  const responseData = error?.response?.data || {};

  if (error?.response?.status === 409 || responseData.reason === 'duplicate_operator_access') {
    return content.form.duplicateMessage;
  }

  return content.form.errorMessage;
};

const AdminOperatorAccessCreatePage = () => {
  const content = yowanPageContent['/admin/access/operator/new'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const memberId = searchParams.get('memberId') || '';
  const memberLabel = searchParams.get('memberLabel') || '';
  const [lockedMemberLabel, setLockedMemberLabel] = useState(memberLabel);
  const [submitMessage, setSubmitMessage] = useState('');
  const [memberOptions, setMemberOptions] = useState(operatorAccessMemberPlaceholderOptions);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [optionLoadError, setOptionLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formContent = memberId
    ? {
        ...content,
        form: {
          ...content?.form,
          memberOptions: [
            { value: memberId, label: lockedMemberLabel || '対象会員を読み込み中' },
          ],
        },
      }
    : {
        ...content,
        form: {
          ...content?.form,
          memberOptions,
        },
      };
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...formContent?.form.defaults,
      memberId: memberId || formContent?.form.defaults?.memberId || '',
    },
  });

  useEffect(() => {
    if (memberLabel) {
      setLockedMemberLabel(memberLabel);
      return undefined;
    }

    if (!memberId) {
      return undefined;
    }

    let isMounted = true;

    fetchYowanAdminMemberDetail(memberId)
      .then((record) => {
        if (isMounted) {
          setLockedMemberLabel(record.name || record.email || '選択済みの会員');
        }
      })
      .catch(() => {
        if (isMounted) {
          setLockedMemberLabel('選択済みの会員');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [memberId, memberLabel]);

  useEffect(() => {
    if (!content || memberId) {
      return undefined;
    }

    let isMounted = true;

    const loadMemberOptions = async () => {
      setIsLoadingOptions(true);
      setOptionLoadError('');

      try {
        const options = await fetchYowanAdminMemberOptions();
        const mergedOptions = [
          { value: '', label: '対象会員を選択' },
          ...options,
        ];

        if (isMounted) {
          setMemberOptions(mergedOptions);
        }
      } catch (error) {
        if (isMounted) {
          setOptionLoadError(content.form.loadErrorMessage);
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadMemberOptions();

    return () => {
      isMounted = false;
    };
  }, [content, memberId]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await createYowanAdminOperatorAccess(values);
      setSubmitMessage(content.form.successMessage);
      navigate(appendYowanPreviewQueryIfNeeded('/admin/access/operator', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getOperatorAccessCreateErrorMessage(error, content));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/access/operator" routeKey="/admin/access/operator/new">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/access/operator', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {isLoadingOptions ? <LoadingSpinner size="sm" /> : null}
        {optionLoadError ? <p className="yowan-admin-form__message">{optionLoadError}</p> : null}
        <AdminAccessEditorFields
          content={formContent}
          register={register}
          errors={errors}
          memberLocked={Boolean(memberId)}
          lockedMemberId={memberId}
        />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting || isLoadingOptions}>
            {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
          </button>
        </div>

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminOperatorAccessCreatePage;
